import { useCallback, useRef } from 'react';

/**
 * Powrót fokusu po zamknięciu kontrolowanego dialogu Radix (bez `Dialog.Trigger`). Radix w `onCloseAutoFocus`
 * blokuje domyślne przywrócenie fokusu i ustawia go na `triggerRef`, który bez Triggera jest pusty — fokus
 * trafiał na `body` i osoba z klawiaturą lub czytnikiem ekranu zaczynała od początku strony.
 *
 * Element z fokusem zapamiętujemy w renderze, w którym `open` zmienia się na `true`: wtedy treść dialogu jeszcze
 * nie istnieje (pole z `autoFocus` i pułapka fokusu Radix przenoszą fokus dopiero przy montowaniu), więc to
 * przycisk albo skrót, który dialog otworzył. `onCloseAutoFocus` przywraca na niego fokus; gdy zniknął z DOM albo
 * nie przyjmuje fokusu — na nagłówek strony (`main h1`), awaryjnie na `main`. Gdy fokus jest już poza dialogiem
 * (strona po przejściu sama ustawiła go w polu, np. „Zanotuj” z menu telefonu → pole na Dziś), zostaje tam.
 */
export function useReturnFocus(open: boolean): (event: Event) => void {
  const wasOpen = useRef(false);
  const returnTo = useRef<HTMLElement | null>(null);
  if (open && !wasOpen.current && typeof document !== 'undefined') {
    const active = document.activeElement;
    returnTo.current = active instanceof HTMLElement && active !== document.body ? active : null;
  }
  wasOpen.current = open;

  return useCallback((event: Event) => {
    event.preventDefault();
    const target = returnTo.current;
    returnTo.current = null;
    // fokus już celowo poza dialogiem (np. pole „Zanotuj…” na Dziś po skrócie z menu telefonu) — zostaje tam
    const current = typeof document !== 'undefined' ? document.activeElement : null;
    if (current instanceof HTMLElement && current !== document.body && !current.closest('[role="dialog"]')) return;
    if (target?.isConnected) {
      target.focus({ preventScroll: true });
      // element wyłączony albo niedostępny (inert) nie przyjmie fokusu
      if (document.activeElement === target) return;
    }
    const fallback = document.querySelector<HTMLElement>('main h1') ?? document.querySelector<HTMLElement>('main');
    if (!fallback) return;
    if (!fallback.hasAttribute('tabindex')) fallback.setAttribute('tabindex', '-1');
    fallback.focus({ preventScroll: true });
  }, []);
}
